import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'

import { useNotification } from '../../../../hooks'
import { useLoadFileById } from '../../../File'
import { FILES_PREVIEW_PDF_PERMANENT_API_PARAMS } from '../../../FilesPreviewGallery'
import {
  DOC_FLOW_MAPPING_ACTIVE_FILE_ID_QUERY_PARAM,
  getDocFlowMappingActivePreviewFile,
  getPreparedActiveFilePreview,
  isFileInProgress,
} from '../../DocFlowMapping'
import { getUploadingItem } from '../../DocFlowUploading'
import { useDocFlowContext } from '../useDocFlowContext'

import { getIsActiveFile } from './useDocFlowMappping.utils'

export function useDocFlowMappping({
  files,
  handleToggleOpenSelectAreaInfo,
  setPdfViewerSelectAreaInitialParams,
  setProcessData,
}) {
  const {
    containerRef,
    docFlow,
    mappingPhaseFileActive,
    setMappingPhaseFileActive,
  } = useDocFlowContext()

  const { handleAddErrorNotification } = useNotification()

  const [searchParams, setSearchParams] = useSearchParams()
  const activeFileQuery = searchParams.get(
    DOC_FLOW_MAPPING_ACTIVE_FILE_ID_QUERY_PARAM
  )

  const { getFileData } = useLoadFileById()

  const [fileBytes, setPdfBytes] = useState(null)
  const [loadingFileBytes, setLoadingFileBytes] = useState(false)
  const [initiatedActiveFile, setInitiatedActiveFile] = useState(false)

  const [isPageLoader, setPageLoader] = useState(false)

  const activeFileForPreview = getPreparedActiveFilePreview({
    files: files,
    mappingPhaseFileActive,
  })

  function handleAddActiveFileToSearchParams(id) {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)

        next.set(DOC_FLOW_MAPPING_ACTIVE_FILE_ID_QUERY_PARAM, id)

        return next
      },
      { replace: true }
    )
  }

  function getFileBufferData(data) {
    setPdfBytes(null)

    fetch(data.origin)
      .then(res => {
        if (res.ok) {
          return res.arrayBuffer()
        }
      })
      .then(setPdfBytes)
      .catch(() => {
        handleAddErrorNotification(
          'Notification.DocumentStaplingMappingResultFileErrorTitleKey'
        )
      })
      .finally(() => {
        setLoadingFileBytes(false)
      })
  }

  async function handleGetFileData(id) {
    setLoadingFileBytes(true)

    return getFileData({
      apiParams: FILES_PREVIEW_PDF_PERMANENT_API_PARAMS,
      id: getDocFlowMappingActivePreviewFile(files, id).file_id,
    }).then(data => {
      getFileBufferData(data)
    })
  }

  useEffect(() => {
    if (files?.length > 0 && !initiatedActiveFile) {
      const fileInProgress = files.find(isFileInProgress)

      const isActiveFile = getIsActiveFile({ activeFileQuery, fileInProgress })

      const activeFile = isActiveFile
        ? activeFileQuery
        : fileInProgress?.id || mappingPhaseFileActive || files[0]?.id

      if (!mappingPhaseFileActive) {
        setMappingPhaseFileActive(Number(activeFile))
      }

      if (!isActiveFile) {
        handleAddActiveFileToSearchParams(activeFile)
      }

      // eslint-disable-next-line react-hooks/set-state-in-effect -- loads active file data and flips the init flag in the async callback
      handleGetFileData(activeFile).finally(() => {
        setInitiatedActiveFile(true)
      })
    }
  }, [files])

  const someFileInProgress = files.some(isFileInProgress)

  async function handleGetActiveFile() {
    return getUploadingItem({
      documentId: docFlow.id,
      itemId: activeFileForPreview.itemId,
    })
  }

  function handleChangeActiveFile(id) {
    if (id !== mappingPhaseFileActive && !someFileInProgress) {
      setMappingPhaseFileActive(id)
      setProcessData(null)
      setPdfViewerSelectAreaInitialParams()
      handleToggleOpenSelectAreaInfo(false)

      handleAddActiveFileToSearchParams(id)

      handleGetFileData(id)
    }
  }

  return {
    activeFileBytes: fileBytes,
    activeFileForPreview,
    containerRef,
    handleChangeActiveFile,
    handleGetActiveFile,
    isPageLoader,
    loadingFileBytes,
    setPageLoader,
    someFileInProgress,
  }
}
